import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { format } from "date-fns";
import { RootState } from "../../../../app/store";
import { Nullable } from "../../../../common/types/nullable";
import {
  AssignmentById,
  assignmentsByTalentId,
  overlappableAssignmentsByTalentId,
  OverlappableContEmp,
} from "../../../talents/talentAPI";
import {
  AssignmentsByTalentIdFilters,
  OverlappableAssignmentsByTalentIdFilters,
} from "../../../talents/types/queries";

type Entities = {
  [key: string]: any;
};

export type SliceState = {
  ids: [];
  entities: Entities;
  status: "idle" | "pending" | "succeeded" | "failed";
  error: Nullable<string>;
  overlaps: OverlappableContEmp[];
};

const assignmentsAdapter = createEntityAdapter<
  AssignmentById,
  AssignmentById["assignmentId"]
>({
  selectId: (assignment) => assignment.assignmentId,
});

export const fetchAssignmentsByTalentId = createAsyncThunk<
  AssignmentById[],
  AssignmentsByTalentIdFilters,
  { state: RootState }
>(
  "talents/fetchAssignmentsByTalentId",
  async (filters: AssignmentsByTalentIdFilters, { requestId }) =>
    await assignmentsByTalentId(filters, { requestId }),
);

export const fetchOverlappableAssignmentsByTalentId = createAsyncThunk<
  OverlappableContEmp[],
  OverlappableAssignmentsByTalentIdFilters,
  { state: RootState }
>(
  "talents/fetchOverlappableAssignmentsByTalentId",
  async (filters, { requestId }) =>
    await overlappableAssignmentsByTalentId(filters, { requestId }),
);

const initialState: SliceState = {
  status: "idle",
  ids: [],
  entities: {},
  error: null,
  overlaps: [],
};

const assignmentsSlice = createSlice({
  name: "assignments",
  initialState: assignmentsAdapter.getInitialState(initialState),
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssignmentsByTalentId.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchAssignmentsByTalentId.fulfilled, (state, action) => {
        assignmentsAdapter.setAll(state, action.payload);
        state.status = "succeeded";
      })
      .addCase(fetchAssignmentsByTalentId.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(fetchOverlappableAssignmentsByTalentId.pending, (state) => {
        state.status = "pending";
      })
      .addCase(
        fetchOverlappableAssignmentsByTalentId.fulfilled,
        (state, action) => {
          state.overlaps = action.payload;
          state.status = "succeeded";
        },
      )
      .addCase(fetchOverlappableAssignmentsByTalentId.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export const selectAssignments = (state: RootState): AssignmentById[] =>
  Object.values(state.assignments.entities);

export const selectPossibleOverlaps = (
  state: RootState,
): OverlappableContEmp[] =>
  state.assignments.overlaps.filter((assignment) => !assignment.failed);

export const selectFailingOverlaps = (
  state: RootState,
): OverlappableContEmp[] =>
  state.assignments.overlaps.filter((assignment) => assignment.failed);

export const selectTranslation = (state: RootState) => state.localized;

// format to option where name is "dd.MM.yyyy - workLocationName - translated workTitle OR workTitleId"
export const formatAssignmentOption = (
  assignment: AssignmentById,
  workTitle: Record<string, string>,
) => {
  return {
    id: assignment.assignmentId,
    name: `${format(assignment.details.start, "dd.MM.yyyy")} - ${assignment.details.workLocationName} - ${workTitle[assignment.details.workTitleId] || assignment.details.workTitleId}`,
  };
};

// select all assignments as options
export const selectAssignmentOptions = () =>
  createSelector(
    [selectAssignments, selectTranslation],
    (assignments, localized) =>
      assignments.map((assignment) =>
        formatAssignmentOption(assignment, localized.workTitle),
      ),
  );

// selected assignments by employmentId
export const selectAssignmentsByEmploymentId = (employmentId: string) =>
  createSelector(selectAssignments, (assignments) =>
    assignments.filter(
      (assignment) => assignment.employmentId === employmentId,
    ),
  );

// select assignments by employmentId as options
export const selectAssignmentOptionsByEmploymentId = (employmentId: string) =>
  createSelector(
    [selectAssignmentsByEmploymentId(employmentId), selectTranslation],
    (assignments, localized) =>
      assignments.map((assignment) =>
        formatAssignmentOption(assignment, localized.workTitle),
      ),
  );

export const { selectById: selectAssignmentById } =
  assignmentsAdapter.getSelectors((state: RootState) => state.assignments);

export const assignmentsReducer = assignmentsSlice.reducer;
